'use client';

import React from 'react';
import { Button } from '@wtc/ui/components/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@wtc/ui/components/tooltip';
import { Share2, Archive, Trash2, RotateCcw } from 'lucide-react';
import type { ChatStatus } from '../lib/db/types';
import { isShareChatEnabled } from '../lib/utils/feature-flags';

interface ChatActionsProps {
  onShare: () => void;
  onArchive: () => void;
  onDelete: () => void;
  status?: ChatStatus;
}

export function ChatActions({ onShare, onArchive, onDelete, status }: ChatActionsProps) {
  const isArchived = status === 'archived';
  const shareEnabled = isShareChatEnabled();
  const archiveLabel = isArchived ? 'Restore chat' : 'Archive chat';

  return (
    <div className="flex gap-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={onArchive}
            variant="ghost"
            size="sm"
            aria-label={archiveLabel}
            className={`p-2 h-auto w-auto transition-colors duration-200 ${
              isArchived
                ? "text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100/20 dark:hover:bg-amber-900/20"
                : "text-muted-foreground hover:text-foreground hover:bg-brand-teal-light/20 dark:hover:bg-brand-teal-medium/20"
            }`}
          >
            {isArchived ? (
              <RotateCcw className="h-5 w-5" />
            ) : (
              <Archive className="h-5 w-5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{archiveLabel}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={onDelete}
            variant="ghost"
            size="sm"
            aria-label="Delete chat"
            className="p-2 h-auto w-auto text-muted-foreground hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/15 transition-colors duration-200"
          >
            <Trash2 className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Delete chat</TooltipContent>
      </Tooltip>
    </div>
  );
}
